import { type JSX, Show, createEffect, createSignal, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Button } from './Button';
import { Calendar } from './DatePicker';
import { Icon } from './Icon';
import { TimePicker } from './TimePicker';

export const localDateTime = (iso: string | null): string => {
  if (!iso) return '';
  const date = new Date(iso);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, -1);
};

export const validDateTime = (value: string) => {
  if (!value) return true;
  if (!/^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d(:[0-5]\d(\.\d{1,3})?)?$/.test(value))
    return false;
  const date = new Date(value);
  return (
    Number.isFinite(date.getTime()) &&
    localDateTime(date.toISOString()).slice(0, 16) === value.slice(0, 16)
  );
};

/** One local date-time field with a shared calendar and clock selection panel. */
export const DateTimePicker = (props: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  disabled?: boolean;
  required?: boolean;
  hint?: string;
  error?: string;
}) => {
  const [open, setOpen] = createSignal(false);
  let dialog: HTMLDivElement | undefined;
  let trigger: HTMLButtonElement | undefined;
  const close = (restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) trigger?.focus();
  };
  const [position, setPosition] = createSignal<JSX.CSSProperties>({});
  const updatePosition = () => {
    const rect = trigger?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(680, window.innerWidth - 16);
    const below = window.innerHeight - rect.bottom - 12;
    const above = rect.top - 12;
    const placeBelow = below >= 240 || below >= above;
    const available = Math.max(120, placeBelow ? below : above);
    setPosition({
      left: `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`,
      top: placeBelow ? `${Math.max(8, rect.bottom + 4)}px` : undefined,
      bottom: placeBelow ? undefined : `${Math.max(8, window.innerHeight - rect.top + 4)}px`,
      width: `${width}px`,
      'max-height': `${Math.min(window.innerHeight - 16, available)}px`,
    });
  };
  createEffect(() => {
    if (!open()) return;
    const outside = (event: MouseEvent) => {
      if (!dialog?.contains(event.target as Node) && !trigger?.contains(event.target as Node))
        close(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
      }
    };
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', onEscape, true);
    onCleanup(() => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', onEscape, true);
    });
  });
  const display = () =>
    !props.value
      ? 'Select date and time…'
      : validDateTime(props.value)
        ? new Date(props.value).toLocaleString('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
            hour12: false,
          })
        : 'Incomplete date or time';
  const hour = () => props.value.split('T')[1]?.split(':')[0] ?? '00';
  const minute = () => props.value.split('T')[1]?.split(':')[1] ?? '00';
  const changeTime = (h: string, m: string) =>
    props.onChange(
      `${props.value.slice(0, 10)}T${h ? h.padStart(2, '0') : ''}:${m ? m.padStart(2, '0') : ''}`,
    );
  return (
    <div class="min-w-0 space-y-1 text-sm">
      <span class="font-medium text-neutral-700 dark:text-neutral-300">{props.label}</span>
      <button
        ref={trigger}
        type="button"
        aria-label={props.label}
        aria-haspopup="dialog"
        aria-expanded={open()}
        disabled={props.disabled}
        aria-required={props.required}
        aria-invalid={Boolean(props.error)}
        class="flex h-10 w-full items-center justify-between gap-3 rounded-lg border border-neutral-300 bg-white px-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-neutral-700 dark:bg-neutral-900"
        onClick={() => {
          if (open()) {
            close();
            return;
          }
          setOpen(true);
          updatePosition();
          queueMicrotask(() =>
            (
              dialog?.querySelector<HTMLButtonElement>(
                'section [aria-pressed="true"]:not(:disabled)',
              ) ?? dialog?.querySelector<HTMLButtonElement>('section button')
            )?.focus(),
          );
        }}
      >
        <span class="truncate" classList={{ 'text-neutral-500': !props.value }}>
          {display()}
        </span>
        <Icon name="calendar" class="shrink-0 text-neutral-400" />
      </button>
      <Portal>
        <div
          ref={dialog}
          aria-label={`Choose ${props.label.toLowerCase()}`}
          role="dialog"
          aria-modal="false"
          aria-hidden={!open()}
          inert={!open()}
          style={position()}
          classList={{
            'visible opacity-100 translate-y-0': open(),
            'invisible opacity-0 translate-y-2': !open(),
          }}
          class="fixed z-[70] max-h-[calc(100dvh-16px)] overflow-y-auto rounded-xl border border-neutral-200 bg-surface p-4 text-neutral-900 shadow-xl transition-[opacity,translate,visibility] duration-200 motion-reduce:transition-none dark:border-neutral-700 dark:bg-surface-dark dark:text-neutral-100"
        >
          <header class="mb-5 flex items-center justify-between gap-3">
            <h2 class="font-semibold">{props.label}</h2>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Close date and time picker"
              onClick={() => close()}
            >
              ✕
            </Button>
          </header>
          <fieldset disabled={props.disabled} class="grid items-start gap-5 sm:grid-cols-2">
            <section aria-label="Date">
              <h3 class="mb-3 text-sm font-medium">Date</h3>
              <Calendar
                value={props.value.slice(0, 10)}
                onChange={(date) =>
                  props.onChange(`${date}T${props.value.split('T')[1] || '00:00'}`)
                }
              />
            </section>
            <TimePicker
              hour={hour()}
              minute={minute()}
              disabled={props.disabled || !props.value}
              onChange={changeTime}
            />
          </fieldset>
          <p class="mt-4 text-xs text-neutral-500">
            24-hour time · {Intl.DateTimeFormat().resolvedOptions().timeZone}
          </p>
          <Show when={!validDateTime(props.value)}>
            <p role="alert" class="mt-2 text-sm text-red-600">
              Choose a valid date, hour (00–23) and minute (00–59).
            </p>
          </Show>
          <footer class="mt-4 flex items-center justify-between border-t border-neutral-200 pt-4 dark:border-neutral-700">
            <Button
              variant="ghost"
              disabled={props.disabled || !props.value}
              onClick={() => props.onChange('')}
            >
              Clear deadline
            </Button>
            <Button onClick={() => close()} disabled={!validDateTime(props.value)}>
              Done
            </Button>
          </footer>
        </div>
      </Portal>
      <Show when={props.error || props.hint}>
        <p class={props.error ? 'text-xs text-red-600' : 'text-xs text-neutral-500'}>
          {props.error || props.hint}
        </p>
      </Show>
      <Show when={!open() && !validDateTime(props.value)}>
        <p role="alert" class="text-xs text-red-600">
          Choose a valid date and time.
        </p>
      </Show>
    </div>
  );
};
